import { useEffect, useState } from 'react'
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined'
import LockOpenOutlinedIcon from '@mui/icons-material/LockOpenOutlined'
import {
  Alert,
  Box,
  Button,
  Chip,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'

import { discussionCsApi, DiscussionCsApiError } from './chapterDiscussions.api'
import { canUnblockDiscussionUser } from './chapterDiscussions.model'
import { DiscussionActionDialog } from './DiscussionActionDialog'
import { DiscussionBulkDeleteJobPanel } from './DiscussionBulkDeleteJobPanel'

import type {
  DiscussionUserEnforcementAction,
  DiscussionUserBulkDeleteJob,
  DiscussionUserEnforcement,
  SerializedTimestamp,
} from './chapterDiscussions.types'

export interface DiscussionUserEnforcementPanelProps {
  userId: number
  userName?: string
  enforcement: DiscussionUserEnforcement | null
  enforcementActions?: DiscussionUserEnforcementAction[]
  actorNamesById?: ReadonlyMap<number, string>
}

type EnforcementAction = 'block' | 'block_and_delete_all' | 'unblock'

const ENFORCEMENT_HISTORY_COLUMNS = [
  { label: 'Time', width: '25%' },
  { label: 'Decision', width: '25%' },
  { label: 'Action by', width: '25%' },
  { label: 'Reason', width: '25%' },
]

export const BLOCK_BUTTON_SX = {
  '--Button-containedBg': '#F96262',
  '--Button-containedBgGradient': 'none',
  '--Button-containedStroke': 'none',
  bgcolor: '#F96262',
  backgroundImage: 'none',
  borderRadius: 1,
  px: 2,
  py: 1,
  fontSize: 14,
  fontWeight: 700,
  lineHeight: 1.5,
  textTransform: 'none',
  '&:hover': {
    '--Button-containedBg': '#E55656',
    bgcolor: '#E55656',
    backgroundImage: 'none',
  },
}

export const UNBLOCK_BUTTON_SX = {
  '--Button-containedBg': '#58A700',
  '--Button-containedBgGradient': 'none',
  '--Button-containedStroke': 'none',
  bgcolor: '#58A700',
  backgroundImage: 'none',
  borderRadius: 1,
  px: 2,
  py: 1,
  fontSize: 14,
  fontWeight: 700,
  lineHeight: 1.5,
  textTransform: 'none',
  '&:hover': {
    '--Button-containedBg': '#4D9200',
    bgcolor: '#4D9200',
    backgroundImage: 'none',
  },
  '&.Mui-disabled': {
    bgcolor: 'action.disabledBackground',
    backgroundImage: 'none',
  },
}

function timestampText(value: SerializedTimestamp | string | undefined) {
  if (!value) return '—'
  if (typeof value === 'string') return new Date(value).toLocaleString()
  const seconds = value._seconds ?? value.seconds
  return seconds === undefined ? '—' : new Date(seconds * 1000).toLocaleString()
}

function enforcementDecision(
  action: DiscussionUserEnforcementAction['action'],
) {
  return action === 'block_and_delete_all'
    ? 'block & delete all comments'
    : action
}

export function DiscussionUserEnforcementPanel({
  userId,
  userName = `User ${userId}`,
  enforcement: initialEnforcement,
  enforcementActions = [],
  actorNamesById = new Map(),
}: DiscussionUserEnforcementPanelProps) {
  const [enforcement, setEnforcement] = useState(initialEnforcement)
  const [currentJob, setCurrentJob] =
    useState<DiscussionUserBulkDeleteJob | null>(null)
  const [action, setAction] = useState<EnforcementAction | null>(null)
  const [pending, setPending] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => setEnforcement(initialEnforcement), [initialEnforcement])

  useEffect(() => {
    let active = true
    const jobId = enforcement?.currentBulkDeleteJobId
    if (!jobId) {
      setCurrentJob(null)
      return
    }
    discussionCsApi
      .getBulkDeleteJob(jobId)
      .then(response => {
        if (active) setCurrentJob(response.job)
      })
      .catch(requestError => {
        if (active) {
          setError(
            requestError instanceof Error
              ? requestError.message
              : 'Unable to load delete-all state',
          )
        }
      })
    return () => {
      active = false
    }
  }, [enforcement?.currentBulkDeleteJobId])

  const submit = async (reason: string) => {
    if (!action) return
    setPending(true)
    setError(null)
    try {
      const response =
        action === 'unblock'
          ? await discussionCsApi.unblockUser(userId, {
              reason,
              expectedGeneration: enforcement?.generation ?? 0,
            })
          : await discussionCsApi.blockUser(userId, { action, reason })
      setEnforcement(response.record)
      setAction(null)
      window.dispatchEvent(new Event('discussion-detail-refresh'))
    } catch (requestError) {
      const apiError = requestError as DiscussionCsApiError
      setError(apiError.message)
      if (apiError.status === 409) {
        setAction(null)
        window.dispatchEvent(new Event('discussion-detail-refresh'))
      }
    } finally {
      setPending(false)
    }
  }

  const blocked = enforcement?.status === 'blocked'
  const blockHistoryCount = enforcementActions.filter(
    item => item.action === 'block' || item.action === 'block_and_delete_all',
  ).length
  const unblockEnabled = canUnblockDiscussionUser(enforcement, currentJob)
  const description =
    action === 'block_and_delete_all'
      ? `Block user ${userId} and resumably delete every discussion comment by this user.`
      : action === 'block'
        ? `Block user ${userId} from future discussion writes. Existing public content remains.`
        : `Unblock user ${userId}. Previously deleted content will not be restored.`

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        borderColor: 'divider',
        borderRadius: 2,
        overflow: 'hidden',
      }}>
      <Typography
        sx={{
          mb: 2,
          fontSize: 20,
          fontWeight: 700,
          lineHeight: 1.5,
        }}>
        Commenter Info
      </Typography>
      <Stack spacing={0.5} sx={{ mb: 2 }}>
        {[`Name: ${userName}`, `User ID: ${userId}`].map(text => (
          <Typography
            key={text}
            sx={{ color: '#5F708F', fontSize: 16, lineHeight: 1.5 }}>
            {text}
          </Typography>
        ))}
        <Typography sx={{ color: '#5F708F', fontSize: 16, lineHeight: 1.5 }}>
          Block history: {blockHistoryCount}
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography sx={{ color: '#5F708F', fontSize: 16, lineHeight: 1.5 }}>
            Current status:
          </Typography>
          <Chip
            label={blocked ? 'blocked' : 'unblocked'}
            size="small"
            sx={{
              bgcolor: '#EBEBEB',
              color: '#5F708F',
              fontSize: 14,
              height: 25,
            }}
          />
        </Stack>
      </Stack>

      {enforcementActions.length > 0 && (
        <Box sx={{ mb: 2, overflowX: 'auto' }}>
          <Table
            size="small"
            sx={{
              width: '100%',
              tableLayout: 'fixed',
              '& .MuiTableCell-root': {
                borderBottom: '1px solid rgba(26, 49, 84, 0.1)',
                color: 'text.primary',
                overflowWrap: 'anywhere',
                pl: 0,
                pr: 3,
              },
              '& .MuiTableCell-root:last-of-type': { pr: 0 },
            }}>
            <colgroup>
              {ENFORCEMENT_HISTORY_COLUMNS.map(column => (
                <col key={column.label} style={{ width: column.width }} />
              ))}
            </colgroup>
            <TableHead>
              <TableRow sx={{ height: 56 }}>
                {ENFORCEMENT_HISTORY_COLUMNS.map(column => (
                  <TableCell
                    key={column.label}
                    sx={{
                      bgcolor: 'background.paper',
                      py: 2,
                      fontSize: 16,
                      fontWeight: 700,
                      lineHeight: 1.5,
                    }}>
                    {column.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {enforcementActions.map((item, index) => (
                <TableRow key={item.actionId ?? index}>
                  <TableCell sx={{ py: 2, fontSize: 16, lineHeight: 1.5 }}>
                    {timestampText(item.createdAt)}
                  </TableCell>
                  <TableCell sx={{ py: 2, fontSize: 16, lineHeight: 1.5 }}>
                    {enforcementDecision(item.action)}
                  </TableCell>
                  <TableCell sx={{ py: 2, fontSize: 16, lineHeight: 1.5 }}>
                    <Link
                      href={`#/user-details/${item.actorId}/show`}
                      underline="hover"
                      sx={{ color: '#F47D42' }}>
                      {actorNamesById.get(item.actorId) ??
                        `User ${item.actorId}`}
                    </Link>
                  </TableCell>
                  <TableCell sx={{ py: 2, fontSize: 16, lineHeight: 1.5 }}>
                    {item.reason || '--'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      <Stack direction="row" spacing={2} flexWrap="wrap">
        {!blocked && (
          <Button
            variant="contained"
            color="error"
            startIcon={<BlockOutlinedIcon />}
            onClick={() => setAction('block')}
            sx={BLOCK_BUTTON_SX}>
            Block user
          </Button>
        )}
        {!blocked && (
          <Button
            variant="contained"
            color="error"
            startIcon={<BlockOutlinedIcon />}
            disabled={pending}
            sx={BLOCK_BUTTON_SX}
            onClick={() => setAction('block_and_delete_all')}>
            Block &amp; delete all comments
          </Button>
        )}
        {blocked && (
          <Button
            variant="contained"
            color="success"
            startIcon={<LockOpenOutlinedIcon />}
            disabled={!unblockEnabled}
            sx={UNBLOCK_BUTTON_SX}
            onClick={() => setAction('unblock')}>
            Unblock user
          </Button>
        )}
      </Stack>
      {blocked && enforcement?.currentBulkDeleteJobId && !unblockEnabled && (
        <Alert severity="info" sx={{ mt: 2 }}>
          Unblock remains disabled until the current delete-all generation is
          complete.
        </Alert>
      )}
      {enforcement?.currentBulkDeleteJobId && (
        <Box sx={{ mt: 2 }}>
          <DiscussionBulkDeleteJobPanel
            jobId={enforcement.currentBulkDeleteJobId}
          />
        </Box>
      )}
      <DiscussionActionDialog
        open={action !== null}
        title={
          action === 'block_and_delete_all'
            ? 'Block and delete all comments'
            : action === 'block'
              ? 'Block discussion user'
              : 'Unblock discussion user'
        }
        description={description}
        confirmLabel={
          action === 'unblock'
            ? 'Unblock'
            : action === 'block'
              ? 'Block'
              : 'Block and delete all'
        }
        destructive={action === 'block_and_delete_all'}
        pending={pending}
        error={error}
        onClose={() => setAction(null)}
        onConfirm={submit}
      />
    </Paper>
  )
}
